import AT from "@/admin/AdminTable.module.css";

import { AccountsDetailView } from "@/admin/accounts/AccountsDetailView.tsx";
import { AccountsToolbar } from "@/admin/accounts/AccountsToolbar.tsx";
import { AddRemoveCreditButton, AddRemoveOngoingButton } from "@/admin/accounts/AddRemoveCreditModal.tsx";
import { AutomatedTopUpBadge } from "@/admin/accounts/AutomatedTopUpBadge.tsx";
import { BillingAccountBlock } from "@/admin/accounts/BillingAccountBlock.tsx";
import { DeleteAccountBlock } from "@/admin/accounts/DeleteAccountBlock.tsx";
import { DiscountModalButton } from "@/admin/accounts/DiscountModal.tsx";
import { InvoiceEmailSendingButton } from "@/admin/accounts/InvoiceEmailSendingModal.tsx";
import { KycStatusBadge, KycStatusBadgeButton, kycStatusDescriptions } from "@/admin/accounts/KycStatusBadge.tsx";
import { PaymentMethod } from "@/admin/accounts/PaymentMethodButton.tsx";
import { PaymentPlanBadge } from "@/admin/accounts/PaymentPlanBadge.tsx";
import { ReferralShareCodeButton } from "@/admin/accounts/ReferralShareCodeModal.tsx";
import {
    levelDescriptions,
    RestrictionLevelBadge,
    RestrictionLevelButton,
} from "@/admin/accounts/RestrictionLevelBadge.tsx";
import { StatusBadge, StatusBadgeButton, statusDescriptions } from "@/admin/accounts/StatusBadge.tsx";
import { SuspensionReasonButton } from "@/admin/accounts/SuspensionReasonModal.tsx";
import { VatModalButton } from "@/admin/accounts/VatModal.tsx";
import { Extra } from "@/admin/AdminTable.tsx";
import { gf, type GqlFieldsOf } from "@/admin/FieldConfig.tsx";
import {
    BooleanComponentFilter,
    DeletedFilter,
    EnabledFilter,
    EnumComponentFilter,
    ExactNumberFilter,
    NumberRangeFilter,
    TextFilter,
    UnixDateRangeFilter,
} from "@/admin/filters.tsx";
import { gql } from "@/admin/graphql.gen/gql.ts";
import {
    BillingAccountOrderFields,
    type GetBillingAccountListQuery,
    OrderDirection,
} from "@/admin/graphql.gen/graphql.ts";
import { extractData } from "@/admin/graphql/extractData.tsx";
import { GraphqlTable } from "@/admin/GraphqlTable.tsx";
import { RelatedItems } from "@/admin/RelatedItems.tsx";
import {
    createdAtField,
    currencyField,
    idField,
    numberField,
    userIdField,
} from "@/admin/table_fields/standardFields.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { ShortDate } from "@/components/l10n/DateFormat.tsx";
import { VatText } from "@/components/VatText.tsx";
import { configAtom } from "@/config.ts";
import { type CommonBillingAccountFields, ECommonBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { CountryFlagTitle } from "@/modules/location/CountryFlag.tsx";
import { useQuery } from "@apollo/client/react";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { BillingAccountStatus, KycStatus, RestrictionLevel } from "@warrenio/api-spec/spec.oats.gen";
import { useAtomValue } from "jotai/react";
import { atom } from "jotai/vanilla";
import { keys } from "remeda";
import type { SetNonNullable } from "type-fest";

const GET_BILLING_ACCOUNTS = gql(/* GraphQL */ `
    query GetBillingAccountList(
        $limit: Int
        $page: Int
        $orderField: BillingAccountOrderFields
        $orderDir: OrderDirection
        $search: String
        $filters: [BillingAccountFilter!]
    ) {
        admin_billing_account_list(
            limit: $limit
            page: $page
            orderField: $orderField
            orderDir: $orderDir
            search: $search
            filters: $filters
        ) {
            items {
                ...BillingAccountBlock
                id
                email
                title
                user_id
                customer_name
                customer_phone
                customer_id_number
                reseller
                company_name
                is_active
                allow_debt
                is_recurring_payment_enabled
                restriction_level
                send_invoice_email
                discount_percentage
                vat_percentage
                suspend_reason
                status
                credit_amount
                precalc_credit_amount
                precalc_days_in_debt
                precalc_ongoing
                created
                referral_code
                referral_share_code
                paying_by_invoice
                additional_data
                integration_id
                is_deleted
                deleted_at
                primary_card {
                    card_holder
                    card_type
                    expire_month
                    expire_year
                    id
                    is_verified
                    is_verification_pending
                    last4
                    type
                }
                running_totals {
                    subtotal
                    ongoing_negative_since
                    ongoing
                    discount_amount
                }
                kyc_info {
                    provider
                    status
                }

                # For detail view
                # TODO: Use a separate query for this?
                company_reg_code
                company_vat_number
                country
                state_code
                county
                city
                address_line1
                post_index
            }
            paging {
                total
            }
        }
    }
`);

export type GQAccountItem = NonNullable<GetBillingAccountListQuery["admin_billing_account_list"]["items"]>[number];

/** GraphQL version of `EBillingAccount` */
class EAdminBillingAccount extends ECommonBillingAccount {
    protected readonly _account: CommonBillingAccountFields;

    constructor(public readonly item: GQAccountItem) {
        super();
        this._account = {
            ...item,
            id: Number(item.id),
            // NB: GraphQL has all fields nullable for some unknown reason, so pretend they're not
        } as SetNonNullable<CommonBillingAccountFields>;
    }
}

/** Enum for referral fields IDs for easily filtering them out later */
enum ReferralFieldIds {
    ReferralCode = "referral_code",
    ReferralShareCode = "referral_share_code",
}

const baseAccountFields: GqlFieldsOf<GQAccountItem, BillingAccountOrderFields> = [
    gf({
        ...idField,
        id: "id",
        title: "ID",
        get: (a) => a.id,
        order: BillingAccountOrderFields.Id,
        filter: ExactNumberFilter,
    }),
    gf({
        ...userIdField,
        get: (a) => a.user_id,
        order: BillingAccountOrderFields.UserId,
    }),
    gf({
        ...createdAtField,
        get: (a) => apiUnixToDate(a.created),
        order: BillingAccountOrderFields.Created,
    }),
    gf({
        id: "deleted_at",
        title: "Deleted At",
        get: (a) => (typeof a.deleted_at === "number" ? apiUnixToDate(a.deleted_at) : null),
        order: BillingAccountOrderFields.DeletedAt,
        filter: UnixDateRangeFilter,
        hidden: true,
    }),
    gf({
        id: "billing_account",
        title: "Name & Email",
        get: (a) => a,
        render: ({ value }) => <BillingAccountBlock value={value} isLink={false} />,
    }),
    gf({
        id: "title",
        title: "Title",
        get: (a) => a.title,
        order: BillingAccountOrderFields.Title,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "customer_name",
        title: "Customer Name",
        get: (a) => a.customer_name,
        order: BillingAccountOrderFields.CustomerName,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "customer_phone",
        title: "Customer Phone",
        get: (a) => a.customer_phone,
        order: BillingAccountOrderFields.CustomerPhone,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "customer_id_number",
        title: "Customer\nID Number",
        get: (a) => a.customer_id_number,
        order: BillingAccountOrderFields.CustomerIdNumber,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "company_name",
        title: "Company Name",
        get: (a) => a.company_name,
        order: BillingAccountOrderFields.CompanyName,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "email",
        title: "Invoice Email",
        get: (a) => a.email,
        order: BillingAccountOrderFields.Email,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        ...currencyField,
        id: "usage_charges",
        title: "Usage\nCharges",
        get: (a) => a.running_totals?.subtotal,
        order: BillingAccountOrderFields.TotalsSubtotal,
        filter: NumberRangeFilter,
    }),
    gf({
        ...currencyField,
        id: "precalc_credit_amount",
        title: "Credit",
        get: (a) => a.precalc_credit_amount,
        render: AddRemoveCreditButton,
        order: BillingAccountOrderFields.PrecalcCreditAmount,
        filter: NumberRangeFilter,
        hidden: true,
    }),
    gf({
        ...currencyField,
        id: "ongoing_balance",
        title: "Ongoing\nBalance",
        get: (a) => a.running_totals?.ongoing ?? 0,
        render: AddRemoveOngoingButton,
        order: BillingAccountOrderFields.PrecalcOngoing,
        filter: NumberRangeFilter,
    }),
    gf({
        id: "days_in_debt",
        title: "Days\nIn Debt",
        get: (a) => a.precalc_days_in_debt,
        order: BillingAccountOrderFields.PrecalcDaysInDebt,
        render: ({ value, item }) => {
            const since = item.running_totals?.ongoing_negative_since;
            return <Extra value={value ?? null}>{!!since && <ShortDate date={apiUnixToDate(since)} />}</Extra>;
        },
        filter: NumberRangeFilter,
    }),
    gf({
        id: "allow_debt",
        title: "Payment Plan",
        get: (a) => a.allow_debt,
        render: PaymentPlanBadge,
        order: BillingAccountOrderFields.AllowDebt,
        filter: () => <BooleanComponentFilter component={PaymentPlanBadge} />,
    }),
    gf({
        id: "payment_method",
        title: "Payment Method",
        get: (a) => new EAdminBillingAccount(a),
        render: PaymentMethod,
    }),
    gf({
        id: "is_recurring_payment_enabled",
        title: "Automated Top Up",
        get: (a) => a.is_recurring_payment_enabled,
        render: AutomatedTopUpBadge,
        order: BillingAccountOrderFields.IsRecurringPaymentEnabled,
        filter: EnabledFilter,
        hidden: true,
    }),
    gf({
        id: "send_invoice_email",
        title: "Invoice Email Sending",
        get: (a) => a.send_invoice_email,
        render: InvoiceEmailSendingButton,
        order: BillingAccountOrderFields.SendInvoiceEmail,
        filter: EnabledFilter,
        hidden: true,
    }),
    gf({
        id: "discount_percentage",
        title: "Discount %",
        get: (a) => a.discount_percentage,
        render: DiscountModalButton,
        order: BillingAccountOrderFields.DiscountPercentage,
        filter: NumberRangeFilter,
        hidden: true,
    }),
    gf({
        ...numberField,
        id: "vat_percentage",
        title: <VatText />,
        get: (a) => a.vat_percentage,
        render: VatModalButton,
        order: BillingAccountOrderFields.VatPercentage,
        filter: NumberRangeFilter,
        hidden: true,
    }),
    gf({
        id: "country",
        title: "Country",
        get: (a) => a.country,
        render: ({ value }) => <CountryFlagTitle code={value} />,
        order: BillingAccountOrderFields.Country,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "restriction_level",
        title: "Restriction Level",
        get: (a) => a.restriction_level as RestrictionLevel,
        order: BillingAccountOrderFields.RestrictionLevel,
        render: RestrictionLevelButton,
        filter: () => <EnumComponentFilter values={keys(levelDescriptions)} component={RestrictionLevelBadge} />,
    }),
    gf({
        id: "status",
        title: "Status",
        get: (a) => a.status as BillingAccountStatus | undefined,
        render: StatusBadgeButton,
        order: BillingAccountOrderFields.Status,
        filter: () => <EnumComponentFilter values={keys(statusDescriptions)} component={StatusBadge} />,
    }),
    gf({
        id: ReferralFieldIds.ReferralCode,
        title: "Campaign Codes Used",
        get: (a) => a.referral_code,
        order: BillingAccountOrderFields.ReferralCode,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: ReferralFieldIds.ReferralShareCode,
        title: "Referral Share Code",
        get: (a) => a.referral_share_code,
        render: ReferralShareCodeButton,
        order: BillingAccountOrderFields.ReferralShareCode,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "suspend_reason",
        title: "Suspend Reason",
        get: (a) => a.suspend_reason,
        render: ({ value, item }) => <SuspensionReasonButton item={item} value={value} />,
        order: BillingAccountOrderFields.SuspendReason,
        filter: TextFilter,
    }),
    gf({
        id: "kyc_provider",
        title: "KYC\nProvider",
        get: (a) => a.kyc_info?.provider,
        order: BillingAccountOrderFields.KycInformationProvider,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "kyc_status",
        title: "KYC\nStatus",
        get: (a) => a.kyc_info?.status as KycStatus | undefined,
        render: KycStatusBadgeButton,
        order: BillingAccountOrderFields.KycInformationStatus,
        filter: () => <EnumComponentFilter values={keys(kycStatusDescriptions)} component={KycStatusBadge} />,
        hidden: true,
    }),
    gf({
        id: "integration_id",
        title: "Integration ID",
        get: (a) => a.integration_id,
        order: BillingAccountOrderFields.IntegrationId,
        filter: TextFilter,
        hidden: true,
    }),
    gf({
        id: "is_deleted",
        title: "Deleted",
        get: (a) => a.is_deleted,
        render: ({ value }) => (value ? "DELETED" : "NOT DELETED"),
        order: BillingAccountOrderFields.IsDeleted,
        filter: DeletedFilter,
        hidden: true,
    }),
];

const accountFieldsAtom = atom((get) => {
    const { referralCodeEnabled } = get(configAtom);
    const referralFields = Object.values(ReferralFieldIds);
    // Filter out referral fields if referral code is not enabled
    return referralCodeEnabled ? baseAccountFields : baseAccountFields.filter((f) => !referralFields.includes(f.id));
});

export function AccountsTable() {
    const accountFields = useAtomValue(accountFieldsAtom);

    return (
        <GraphqlTable<GQAccountItem, BillingAccountOrderFields>
            title="Billing Accounts"
            fields={accountFields}
            defaults={{
                orderField: BillingAccountOrderFields.Created,
                orderDir: OrderDirection.Desc,
            }}
            getId={(item) => item.id}
            useQuery={(variables) => {
                // eslint-disable-next-line react-hooks/rules-of-hooks
                const q = useQuery(GET_BILLING_ACCOUNTS, { variables });
                return extractData(q, (d) => d.admin_billing_account_list);
            }}
            getRowClassname={(item) =>
                item.is_deleted ? AT.Accounts_Deleted : item.status === "SUSPENDED" ? AT.Accounts_Suspended : undefined
            }
            actionButtons={(item) => (
                <>
                    <RelatedItems id={item.id} from="fromAccount" inTable />
                    <DeleteAccountBlock
                        item={item}
                        button={
                            <WModalButton
                                isDisabled={!!item.is_deleted}
                                inTable
                                label="Delete"
                                size="xs"
                                icon="jp-trash-icon"
                            />
                        }
                    />
                </>
            )}
            renderDetailToolbar={(item) => <AccountsToolbar item={item} />}
            renderDetail={(item) => <AccountsDetailView item={item} />}
        />
    );
}
